import { http } from './http';
import { auth } from './firebase';

export interface AgentCommandResult {
  success: boolean;
  status: 'executed' | 'clarification_required' | 'unsupported' | 'proposed' | 'discarded';
  proposal?: { id: string; toolName: string; parameters: Record<string, unknown>; description: string };
  message?: string;
  result?: {
    actionId?: string | null;
    toolName?: string;
    result?: Record<string, unknown>;
  };
  clarification?: {
    reason: 'AMBIGUOUS_BOOKING' | 'MISSING_ENTITY' | 'SOURCE_MISMATCH' | 'UNSUPPORTED_COMMAND';
    question: string;
    options: Array<{
      bookingReference: string;
      guestName: string;
      roomNumber?: string;
      checkIn?: string;
      checkOut?: string;
      status?: string;
    }>;
  };
  correlationId?: string;
}

class AgentCommandService {
  private getApiInstance() {
    return http;
  }

  async execute(
    message: string,
    context?: { propertyKeys?: string[]; previousCommand?: string },
    confirmation?: { sessionId: string; proposalId?: string; decision?: 'execute' | 'discard' | 'end_session' }
  ): Promise<AgentCommandResult> {
    const user = auth.currentUser;
    let response;
    try {
      response = await this.getApiInstance().post('/api/agent/commands/execute', {
        actor: {
          id: user?.uid || user?.phoneNumber || 'dashboard-user',
          role: 'admin',
        },
        channel: {
          type: 'web',
          id: 'dashboard',
          ...(confirmation ? { sessionId: confirmation.sessionId } : {}),
        },
        message,
        ...(confirmation?.decision ? { decision: confirmation.decision, proposalId: confirmation.proposalId } : {}),
        ...(context ? { context } : {}),
      });
    } catch (error) {
      const failure = new Error(resolveAgentCommandErrorMessage(error)) as Error & { code?: string };
      failure.code = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      throw failure;
    }

    return response.data as AgentCommandResult;
  }
}

export const agentCommandService = new AgentCommandService();

function resolveAgentCommandErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { message?: unknown; error?: unknown } };
      message?: unknown;
    };

    if (typeof maybeResponse.response?.data?.message === 'string') {
      return maybeResponse.response.data.message;
    }

    if (typeof maybeResponse.response?.data?.error === 'string') {
      return maybeResponse.response.data.error;
    }

    if (typeof maybeResponse.message === 'string') {
      return maybeResponse.message;
    }
  }

  return 'Agent-Anfrage fehlgeschlagen';
}
